import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { teamMembers } from '../data/team';

const Home = () => {
  const expertiseAreas = [
    {
      icon: "Globe",
      title: "Digital Community & Trust",
      description: "Building authentic connections in a digital world. We specialize in managing large-scale online communities, fostering engagement through faith-based initiatives, and maintaining long-term audience trust."
    },
    {
      icon: "Lock",
      title: "Governance & Security",
      description: "Protecting assets while enabling growth. Our approach to security and data governance is embedded in the architecture, ensuring that compliance is a natural byproduct of good design."
    },
    {
      icon: "Activity",
      title: "Operational Resilience",
      description: "Systems that stand the test of time. From infrastructure reliability to disaster recovery, we design operations that maintain continuity under pressure."
    },
    {
      icon: "Anchor",
      title: "Strategic Delivery",
      description: "Turning vision into reality. We employ rigorous program management methodologies to align stakeholders, manage risk, and deliver complex enterprise initiatives on time."
    }
  ];

  const handleScroll = (e, target) => {
    e.preventDefault();
    const element = document.querySelector(target);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
    }
  };

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <header id="home" className="min-h-screen flex flex-col justify-center px-6 relative pt-20">
        <div className="max-w-7xl mx-auto w-full z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-5xl md:text-8xl lg:text-9xl font-medium tracking-tighter text-slate-900 mb-10 leading-[0.9]">
              Systems.<br />
              Scale.<br />
              <span className="text-slate-400">Trust.</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-500 max-w-2xl leading-relaxed mb-12 font-light">
              The Villarosa Collective is a multidisciplinary group of professionals unifying platform engineering, delivery, and digital community influence. Operating from <strong>London, Madrid, Manila, and Germany</strong>.
            </p>
            <div className="flex flex-wrap gap-6">
              <button 
                onClick={(e) => handleScroll(e, '#team')} 
                className="px-10 py-5 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-all flex items-center group shadow-xl shadow-slate-900/10"
              >
                Meet the Collective
                <SafeIcon name="ArrowRight" className="ml-3 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={(e) => handleScroll(e, '#expertise')} 
                className="px-10 py-5 bg-white/50 backdrop-blur-sm border border-slate-200 text-slate-900 rounded-full font-medium hover:bg-white transition-all"
              >
                Our Expertise
              </button>
            </div>
          </motion.div>
        </div>
        
        <div className="absolute right-[-5%] top-1/2 -translate-y-1/2 w-[60%] h-full opacity-[0.03] pointer-events-none">
          <svg viewBox="0 0 100 100" className="w-full h-full fill-slate-900">
            <rect x="10" y="10" width="80" height="80" rx="40" stroke="currentColor" strokeWidth="0.5" fill="none" />
            <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="0.5" fill="none" />
          </svg>
        </div>
      </header>

      {/* Intro Grid */}
      <section className="py-32 bg-white/30 backdrop-blur-sm border-y border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-24"
          >
            {[
              { icon: "Layers", title: "Platform & Data", desc: "Architecting robust ecosystems where security and data governance converge seamlessly." },
              { icon: "Cpu", title: "Systems & Ops", desc: "Ensuring operational continuity through resilient infrastructure and modern reliability standards." },
              { icon: "Users", title: "Community & Influence", desc: "Building long-term digital trust through large-scale strategic engagement and moderation." }
            ].map((item, i) => (
              <motion.div key={i} variants={itemVariants} className="group cursor-default">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-slate-900 mb-8 shadow-sm border border-slate-100 group-hover:border-slate-300 transition-all duration-500 group-hover:-translate-y-1">
                  <SafeIcon name={item.icon} size={28} />
                </div>
                <h3 className="text-2xl font-medium text-slate-900 mb-4 tracking-tight">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed font-light">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-40 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="mb-32"
          >
            <span className="text-sm font-semibold tracking-[0.3em] text-slate-400 uppercase block mb-6">Our Capabilities</span>
            <h2 className="text-5xl md:text-7xl font-medium text-slate-900 mb-8 tracking-tighter leading-tight">Shared Expertise</h2>
            <p className="text-xl text-slate-500 max-w-2xl leading-relaxed font-light">
              Our collective strength lies in a unified approach to systems thinking, governance, and the human element of technology across global hubs.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-32">
            {expertiseAreas.map((area, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="flex flex-col items-start group"
              >
                <div className="mb-8 p-5 bg-white rounded-3xl text-slate-900 shadow-xl shadow-slate-200/50 border border-slate-100 group-hover:scale-105 transition-transform duration-500">
                  <SafeIcon name={area.icon} size={36} />
                </div>
                <h3 className="text-3xl font-medium text-slate-900 mb-6 tracking-tight">{area.title}</h3>
                <p className="text-slate-500 leading-relaxed text-lg font-light">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-40 bg-white shadow-[0_-1px_0_0_rgba(0,0,0,0.05)]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-32 flex flex-col md:flex-row md:items-end justify-between gap-8"
          >
            <div className="max-w-2xl">
              <span className="text-sm font-semibold tracking-[0.3em] text-slate-400 uppercase block mb-6">Leadership</span>
              <h2 className="text-5xl md:text-7xl font-medium text-slate-900 tracking-tighter">The Collective</h2>
            </div>
          </motion.div>
          
          <div className="grid gap-32">
            {teamMembers.map((member) => (
              <article key={member.id} className="group">
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.8 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start border-t border-slate-100 pt-16"
                >
                  <div className="md:col-span-4">
                     <div className="w-28 h-28 bg-slate-50 rounded-full flex items-center justify-center text-2xl text-slate-400 font-light mb-8 group-hover:bg-slate-900 group-hover:text-white transition-all duration-700 ease-[0.16,1,0.3,1]">
                        {member.initials}
                     </div>
                     <h3 className="text-3xl font-medium text-slate-900 mb-3 tracking-tight">{member.name}</h3>
                     <p className="text-slate-400 font-medium mb-8 text-sm uppercase tracking-widest">{member.role}</p>
                     <div className="flex space-x-6 text-slate-300">
                       {member.linkedin && (
                         <a href={member.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} LinkedIn`} className="hover:text-slate-900 transition-all duration-300 hover:scale-110">
                           <SafeIcon name="Linkedin" size={22} />
                         </a>
                       )}
                       {member.facebook && (
                         <a href={member.facebook} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} Facebook`} className="hover:text-slate-900 transition-all duration-300 hover:scale-110">
                           <SafeIcon name="Facebook" size={22} />
                         </a>
                       )}
                       <a href="mailto:hello@villarosa.tech" aria-label={`Email ${member.name}`} className="hover:text-slate-900 transition-all duration-300 hover:scale-110">
                         <SafeIcon name="Mail" size={22} />
                       </a>
                     </div>
                  </div>
                  <div className="md:col-span-8">
                    <p className="text-xl text-slate-600 leading-relaxed mb-12 font-light">
                      {member.bio}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {member.expertise.map((skill) => (
                        <div key={skill} className="flex items-center space-x-3 text-slate-500">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                          <span className="text-sm font-medium tracking-tight uppercase tracking-widest">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-40 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <span className="text-sm font-semibold tracking-[0.3em] text-slate-400 uppercase block mb-6">Inquiries</span>
              <h2 className="text-5xl md:text-7xl font-medium text-slate-900 mb-10 tracking-tighter">Get in Touch</h2>
              <p className="text-xl text-slate-500 mb-16 max-w-md font-light leading-relaxed">
                Whether you need strategic guidance on platform architecture or community management, we're here to help.
              </p>
              <address className="space-y-12 not-italic">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-[0.2em] mb-4">Direct Email</h4>
                  <a href="mailto:hello@villarosa.tech" className="text-2xl text-slate-400 hover:text-slate-900 transition-all duration-300 font-light border-b border-slate-200 hover:border-slate-900 pb-1">
                    hello@villarosa.tech
                  </a>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-[0.2em] mb-4">Global Hubs</h4>
                  <p className="text-2xl text-slate-600 font-light">
                    London <span className="text-slate-200 mx-1">/</span> Madrid <span className="text-slate-200 mx-1">/</span> Manila <span className="text-slate-200 mx-1">/</span> Germany
                  </p>
                </div>
              </address>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="bg-white p-12 md:p-16 rounded-[2.5rem] shadow-2xl shadow-slate-200/40 border border-slate-100 relative"
            >
              <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label htmlFor="firstName" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                    <input id="firstName" type="text" className="w-full px-0 py-3 bg-transparent border-b border-slate-200 focus:outline-none focus:border-slate-900 transition-all" placeholder="Jane" />
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="lastName" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                    <input id="lastName" type="text" className="w-full px-0 py-3 bg-transparent border-b border-slate-200 focus:outline-none focus:border-slate-900 transition-all" placeholder="Doe" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input id="email" type="email" className="w-full px-0 py-3 bg-transparent border-b border-slate-200 focus:outline-none focus:border-slate-900 transition-all" placeholder="jane@company.com" />
                </div>
                <div className="space-y-3">
                  <label htmlFor="message" className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Message</label>
                  <textarea id="message" rows="4" className="w-full px-0 py-3 bg-transparent border-b border-slate-200 focus:outline-none focus:border-slate-900 transition-all resize-none" placeholder="Briefly describe your project..." />
                </div>
                <button type="submit" className="w-full py-5 bg-slate-900 text-white font-medium rounded-2xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 active:scale-[0.98]">
                  Start a Conversation
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;